// Mobile-optimized survey page with camera flow
// Large touch-friendly controls for easy photo capture on iPhone

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  Image,
  Check,
  ChevronRight,
  Trash2,
  Eye,
  Play,
  X,
  Loader2,
  Sparkles,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  Edit3,
  Building2,
  MapPin,
  User
} from 'lucide-react'
import useSurveyStore from '../../stores/surveyStore'
import useCategoryStore from '../../stores/categoryStore'
import { surveyApi } from '../../services/apiService'
import { CameraCapture } from '../survey'
import { Modal, ConfirmModal } from '../common'
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../../utils/constants'
import { SurveyDocument } from '../../pdf/templates/SurveyDocument'
import { pdf } from '@react-pdf/renderer'

// Get image URL - matches PhotoCard logic
function getImageUrl(photo) {
  return photo.thumbnailUrl || photo.imageUrl || photo.thumbnail || photo.imageData || ''
}

export default function MobileSurveyPage({ surveyId }) {
  const navigate = useNavigate()

  const [selectedCategory, setSelectedCategory] = useState(null)
  const [showCamera, setShowCamera] = useState(false)
  const [showPhotos, setShowPhotos] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeTab, setActiveTab] = useState('categories') // 'categories' | 'photos' | 'actions'
  const [generatingPdf, setGeneratingPdf] = useState(false)

  // Analysis state
  const [showAnalysisModal, setShowAnalysisModal] = useState(false)
  const [analysisLoading, setAnalysisLoading] = useState(false)
  const [analysisError, setAnalysisError] = useState(null)

  // Photo notes editing state
  const [editingPhoto, setEditingPhoto] = useState(null)
  const [editingNotes, setEditingNotes] = useState('')

  // Survey details editing state
  const [showEditDetails, setShowEditDetails] = useState(false)
  const [editFormData, setEditFormData] = useState({})
  const [savingDetails, setSavingDetails] = useState(false)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const currentAiResponses = useSurveyStore(state => state.currentAiResponses)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const deleteSurvey = useSurveyStore(state => state.deleteSurvey)
  const addPhoto = useSurveyStore(state => state.addPhoto)
  const deletePhoto = useSurveyStore(state => state.deletePhoto)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)
  const togglePhotoAnalyse = useSurveyStore(state => state.togglePhotoAnalyse)
  const updatePhotoNotes = useSurveyStore(state => state.updatePhotoNotes)
  const markAnalysed = useSurveyStore(state => state.markAnalysed)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)

  const categories = useCategoryStore(state => state.getActiveCategories())
  const customCategories = useCategoryStore(state => state.customCategories)
  const loadCategories = useCategoryStore(state => state.loadCategories)

  useEffect(() => {
    if (surveyId) {
      loadSurvey(surveyId)
      loadCategories()
    }
    return () => clearCurrentSurvey()
  }, [surveyId, loadSurvey, loadCategories, clearCurrentSurvey])

  const handleCapture = useCallback(async (photoData) => {
    if (!selectedCategory) return
    await addPhoto({
      ...photoData,
      categoryCode: selectedCategory.code
    })
    setShowCamera(false)
  }, [selectedCategory, addPhoto])

  const handleDeleteSurvey = async () => {
    await deleteSurvey(surveyId)
    navigate('/surveys')
  }

  const handleDeletePhoto = async (photoId) => {
    await deletePhoto(photoId)
  }

  // Toggle AI analysis flag on a photo
  const handleToggleAI = async (photoId, currentValue) => {
    try {
      await togglePhotoAnalyse(photoId, !currentValue)
    } catch (error) {
      console.error('Failed to toggle AI flag:', error)
    }
  }

  // Open notes editor for a photo
  const handleEditNotes = (photo) => {
    setEditingPhoto(photo)
    setEditingNotes(photo.notes || '')
  }

  // Save photo notes
  const handleSaveNotes = async () => {
    if (!editingPhoto) return
    try {
      await updatePhotoNotes(editingPhoto.id, editingNotes)
      setEditingPhoto(null)
      setEditingNotes('')
    } catch (error) {
      console.error('Failed to save notes:', error)
    }
  }

  // Open edit details modal
  const handleOpenEditDetails = () => {
    setEditFormData({
      propertyName: currentSurvey.propertyName || '',
      address: currentSurvey.address || '',
      postcode: currentSurvey.postcode || '',
      surveyorName: currentSurvey.surveyorName || '',
      surveyLevel: currentSurvey.surveyLevel || 1
    })
    setShowEditDetails(true)
  }

  // Save survey details
  const handleSaveDetails = async () => {
    if (!currentSurvey) return
    setSavingDetails(true)
    try {
      await updateSurvey(currentSurvey.id, editFormData)
      setShowEditDetails(false)
    } catch (error) {
      console.error('Failed to save survey details:', error)
      alert('Failed to save changes. Please try again.')
    } finally {
      setSavingDetails(false)
    }
  }

  // Run AI analysis on all photos marked for analysis
  const handleRunAnalysis = async () => {
    if (!currentSurvey || currentPhotos.length === 0) return

    const photosForAnalysis = currentPhotos.filter(p => p.analyseWithAI !== false)
    if (photosForAnalysis.length === 0) {
      setAnalysisError('No photos marked for AI analysis. Tap the sparkle icon on photos to include them.')
      setShowAnalysisModal(true)
      return
    }

    setAnalysisLoading(true)
    setAnalysisError(null)
    setShowAnalysisModal(true)

    try {
      const results = await surveyApi.analyse(currentSurvey.id)
      await markAnalysed(results)
      setAnalysisLoading(false)
      // Keep modal open briefly to show success
      setTimeout(() => setShowAnalysisModal(false), 1500)
    } catch (err) {
      console.error('Analysis failed:', err)
      setAnalysisError(err.message || 'Analysis failed. Please try again.')
      setAnalysisLoading(false)
    }
  }

  // Generate PDF and share/open on iOS
  const handlePreviewPdf = async () => {
    if (!currentSurvey || generatingPdf) return

    // Open window immediately (before async) to avoid popup blocker
    const pdfWindow = window.open('about:blank', '_blank')
    if (pdfWindow) {
      pdfWindow.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui"><p>Generating PDF...</p></body></html>')
    }

    setGeneratingPdf(true)
    try {
      // Build categoryData from AI responses and saved review data
      const categoryData = {}

      // Get AI responses
      if (currentAiResponses && currentAiResponses.length > 0) {
        const sortedResponses = [...currentAiResponses]
          .filter(r => r.success)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

        for (const response of sortedResponses) {
          if (!categoryData[response.categoryCode]) {
            categoryData[response.categoryCode] = {
              comment: response.response || '',
              rating: null
            }
          }
        }
      }

      // Override with saved review data if exists
      if (currentSurvey.reviewData) {
        for (const [code, data] of Object.entries(currentSurvey.reviewData)) {
          categoryData[code] = {
            comment: data.comment ?? categoryData[code]?.comment ?? '',
            rating: data.rating ?? null
          }
        }
      }

      const blob = await pdf(
        <SurveyDocument
          survey={currentSurvey}
          photos={currentPhotos}
          categoryData={categoryData}
          customCategories={customCategories}
        />
      ).toBlob()

      // Navigate the already-open window to the PDF
      const url = URL.createObjectURL(blob)
      if (pdfWindow) {
        pdfWindow.location.href = url
      }
      // Don't revoke immediately - let the window load it
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      if (pdfWindow) {
        pdfWindow.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui;color:red"><p>Error: ' + error.message + '</p></body></html>')
      }
      alert('PDF error: ' + error.message)
    } finally {
      setGeneratingPdf(false)
    }
  }

  // Get photo count per category
  const getPhotosForCategory = (categoryCode) => {
    return currentPhotos.filter(p => p.categoryCode === categoryCode)
  }

  if (loading || !currentSurvey) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="bg-primary-700 text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/surveys')} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold">Loading...</h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  const totalPhotos = currentPhotos.length
  const categoriesWithPhotos = new Set(currentPhotos.map(p => p.categoryCode)).size

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Header */}
      <div
        className="bg-primary-700 text-white px-4 py-3 flex-shrink-0"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/surveys')}
            className="p-2 -ml-2 hover:bg-primary-600 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">{currentSurvey.propertyName}</h1>
            <div className="flex items-center gap-2 text-sm text-primary-200">
              <span>{totalPhotos} photos</span>
              <span>|</span>
              <span>{categoriesWithPhotos} categories</span>
            </div>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-medium ${SURVEY_STATUS_COLORS[currentSurvey.status]}`}>
            {SURVEY_STATUS_LABELS[currentSurvey.status]}
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-white border-b border-slate-200 flex-shrink-0">
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'categories'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-slate-500'
          }`}
        >
          Categories
        </button>
        <button
          onClick={() => setActiveTab('photos')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'photos'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-slate-500'
          }`}
        >
          Photos ({totalPhotos})
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'actions'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-slate-500'
          }`}
        >
          Actions
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="p-4 space-y-2">
            <p className="text-sm text-slate-500 mb-3">
              Select a category, then tap the camera button to take a photo
            </p>
            {categories.map((category, index) => {
              const photos = getPhotosForCategory(category.code)
              const isSelected = selectedCategory?.code === category.code
              return (
                <button
                  key={category.code}
                  onClick={() => setSelectedCategory(isSelected ? null : category)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                    isSelected
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center text-xs font-bold bg-primary-100 text-primary-700 rounded-full">
                        {index + 1}
                      </span>
                      <span className="text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {category.code}
                      </span>
                      <span className="font-semibold text-slate-900">{category.name}</span>
                    </div>
                    {photos.length > 0 && (
                      <div className="text-sm text-slate-500 mt-1">
                        {photos.length} photo{photos.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  {isSelected ? (
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  ) : photos.length > 0 ? (
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Image className="w-4 h-4 text-emerald-600" />
                    </div>
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Photos Tab */}
        {activeTab === 'photos' && (
          <div className="p-4">
            {/* Summary bar */}
            {currentPhotos.length > 0 && (
              <div className="flex items-center justify-between mb-3 p-2 bg-slate-100 rounded-lg text-sm">
                <span className="text-slate-600">
                  {currentPhotos.filter(p => p.analyseWithAI !== false).length} of {currentPhotos.length} for AI
                </span>
                <span className="text-slate-500 text-xs">Tap sparkle to toggle AI</span>
              </div>
            )}

            {currentPhotos.length === 0 ? (
              <div className="text-center py-12">
                <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No photos yet</h3>
                <p className="text-slate-500 mb-4">Select a category and start capturing</p>
                <button
                  onClick={() => setActiveTab('categories')}
                  className="px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold"
                >
                  Select Category
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {currentPhotos.map((photo) => {
                  const category = categories.find(c => c.code === photo.categoryCode)
                  const analyseWithAI = photo.analyseWithAI !== false
                  return (
                    <div key={photo.id} className="relative">
                      <img
                        src={getImageUrl(photo)}
                        alt={category?.name || 'Photo'}
                        className="w-full aspect-square object-cover rounded-xl"
                      />
                      {/* Category label */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 rounded-b-xl">
                        <div className="text-white text-xs font-medium truncate">
                          {category?.name || photo.categoryCode}
                        </div>
                      </div>

                      {/* Top action buttons */}
                      <div className="absolute top-2 left-2 right-2 flex justify-between">
                        {/* Left: AI toggle + Notes */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleToggleAI(photo.id, analyseWithAI)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                              analyseWithAI
                                ? 'bg-blue-500 text-white'
                                : 'bg-white/90 text-slate-400'
                            }`}
                            title={analyseWithAI ? 'AI analysis ON' : 'AI analysis OFF'}
                          >
                            <Sparkles className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditNotes(photo)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg ${
                              photo.notes
                                ? 'bg-amber-500 text-white'
                                : 'bg-white/90 text-slate-400'
                            }`}
                            title={photo.notes ? 'Has notes' : 'Add notes'}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Right: Delete */}
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === 'actions' && (
          <div className="p-4 space-y-3">
            {/* Edit Details Button */}
            <button
              onClick={handleOpenEditDetails}
              className="w-full p-4 bg-white border border-slate-200 rounded-xl flex items-center gap-4 active:bg-slate-50"
            >
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Edit3 className="w-6 h-6 text-slate-600" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-slate-900">Edit Details</div>
                <div className="text-sm text-slate-500">Address, surveyor, level</div>
              </div>
              <ChevronRight className="w-6 h-6 text-slate-400" />
            </button>

            <hr className="my-2" />

            {/* Analysis status indicator */}
            {currentSurvey.status === 'analysed' && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Survey has been analysed</span>
              </div>
            )}

            <button
              onClick={handleRunAnalysis}
              disabled={currentPhotos.length === 0}
              className={`w-full p-4 rounded-xl flex items-center gap-4 ${
                currentPhotos.length === 0
                  ? 'bg-slate-300 text-slate-500'
                  : 'bg-emerald-600 text-white active:bg-emerald-700'
              }`}
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                currentPhotos.length === 0 ? 'bg-slate-400' : 'bg-emerald-500'
              }`}>
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">
                  {currentSurvey.status === 'analysed' ? 'Re-analyse Survey' : 'Analyse Survey'}
                </div>
                <div className={`text-sm ${currentPhotos.length === 0 ? 'text-slate-400' : 'text-emerald-200'}`}>
                  {currentPhotos.filter(p => p.analyseWithAI !== false).length} photos for AI analysis
                </div>
              </div>
              <ChevronRight className="w-6 h-6" />
            </button>

            <button
              onClick={handlePreviewPdf}
              disabled={generatingPdf}
              className={`w-full p-4 bg-blue-600 text-white rounded-xl flex items-center gap-4 ${generatingPdf ? 'opacity-75' : 'active:bg-blue-700'}`}
            >
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                {generatingPdf ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Eye className="w-6 h-6" />
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">{generatingPdf ? 'Generating...' : 'Preview PDF'}</div>
                <div className="text-sm text-blue-200">{generatingPdf ? 'Opening in viewer' : 'Opens in PDF viewer'}</div>
              </div>
            </button>

            <hr className="my-4" />

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl flex items-center gap-4 active:bg-red-100"
            >
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Delete Survey</div>
                <div className="text-sm text-red-400">Remove this survey permanently</div>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Bottom Camera Button - Always visible when on categories tab */}
      {activeTab === 'categories' && (
        <div
          className="flex-shrink-0 p-4 bg-white border-t border-slate-200"
          style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={() => setShowCamera(true)}
            disabled={!selectedCategory}
            className={`w-full py-4 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              selectedCategory
                ? 'bg-primary-600 text-white active:bg-primary-700'
                : 'bg-slate-200 text-slate-400'
            }`}
          >
            <Camera className="w-6 h-6 flex-shrink-0" />
            <span className="truncate">
              {selectedCategory ? `Take Photo - ${selectedCategory.name}` : 'Select a Category First'}
            </span>
          </button>
        </div>
      )}

      {/* Camera Modal - Full Screen */}
      <Modal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        size="fullscreen"
        showCloseButton={false}
      >
        <CameraCapture
          selectedCategory={selectedCategory}
          onCapture={handleCapture}
          onCancel={() => setShowCamera(false)}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteSurvey}
        title="Delete Survey"
        message={`Are you sure you want to delete "${currentSurvey.propertyName}"? This will also delete all ${totalPhotos} photos. This action cannot be undone.`}
        confirmText="Delete Survey"
        variant="danger"
      />

      {/* Analysis Progress Modal */}
      <Modal
        isOpen={showAnalysisModal}
        onClose={() => !analysisLoading && setShowAnalysisModal(false)}
        title="AI Analysis"
        size="md"
      >
        <div className="py-6">
          {analysisError ? (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysis Failed</h3>
              <p className="text-slate-600 text-center mb-4 px-4">{analysisError}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAnalysisModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700"
                >
                  Close
                </button>
                <button
                  onClick={handleRunAnalysis}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : analysisLoading ? (
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 border-4 border-primary-100 rounded-full" />
                <div className="absolute top-0 left-0 w-20 h-20 border-4 border-primary-600 rounded-full border-t-transparent animate-spin" />
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysing Photos...</h3>
              <p className="text-slate-600 text-center">GPT-4 Vision is analysing your survey photos</p>
              <p className="text-sm text-slate-400 mt-4">This may take a few minutes...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Analysis Complete!</h3>
              <p className="text-slate-600 text-center">AI analysis has been saved. View PDF to see results.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Notes Editing Modal */}
      <Modal
        isOpen={!!editingPhoto}
        onClose={() => setEditingPhoto(null)}
        title="Photo Notes"
        size="md"
      >
        {editingPhoto && (
          <div className="space-y-4">
            <img
              src={getImageUrl(editingPhoto)}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg mx-auto"
            />
            <p className="text-xs text-center text-slate-500">
              Notes are included in the AI analysis prompt for additional context.
            </p>
            <textarea
              value={editingNotes}
              onChange={(e) => setEditingNotes(e.target.value)}
              placeholder="Add notes about this photo (e.g., 'damp patch in corner', 'crack above window')..."
              rows={4}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setEditingPhoto(null)}
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium"
              >
                Save Notes
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Details Modal */}
      <Modal
        isOpen={showEditDetails}
        onClose={() => !savingDetails && setShowEditDetails(false)}
        title="Edit Survey Details"
        size="md"
      >
        <div className="space-y-4">
          {/* Property Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Property Name
            </label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={editFormData.propertyName || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, propertyName: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Smith Residence"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Full Address
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
              <textarea
                value={editFormData.address || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, address: e.target.value }))}
                rows={2}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="e.g., 123 High Street, London"
              />
            </div>
          </div>

          {/* Postcode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Postcode
            </label>
            <input
              type="text"
              value={editFormData.postcode || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, postcode: e.target.value.toUpperCase() }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase"
              placeholder="e.g., SW1A 1AA"
            />
          </div>

          {/* Surveyor Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Surveyor Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={editFormData.surveyorName || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, surveyorName: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter surveyor name"
              />
            </div>
          </div>

          {/* Survey Level */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Survey Level
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 1, label: 'Level 1', desc: 'Basic' },
                { value: 2, label: 'Level 2', desc: 'Standard' },
                { value: 3, label: 'Level 3', desc: 'Full' }
              ].map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => setEditFormData(prev => ({ ...prev, surveyLevel: level.value }))}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    editFormData.surveyLevel === level.value
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-600'
                  }`}
                >
                  <div className="font-semibold text-sm">{level.label}</div>
                  <div className="text-xs opacity-75">{level.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowEditDetails(false)}
              disabled={savingDetails}
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveDetails}
              disabled={savingDetails}
              className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingDetails ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
